import React from 'react';
import get from 'lodash.get';
import { defineMessages, FormattedList, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { GuidService } from 'ui-components/services/guid-service';

import layout from './details-layout';
import * as ShipmentDetailsHelper from './shipment-details-helper';
import style from './shipment-details.module.less';
import LabeledData from '../../components/shipment-configuration-widget/labeled-data';
import { FulfillmentPlanService } from '../../services/fulfillment-plan-service';
import { OtherShippingOptionsService } from '../../services/other-shipping-options/other-shipping-options-service';
import Global from '../../store/global-selectors';

const messages = defineMessages({
  shipFrom: {
    description: 'shipmentDetails.labels.shippedFrom',
    defaultMessage: 'Shipped From',
    id: '23EGtR',
  },
  shipTo: {
    description: 'shipmentDetails.labels.shipTo',
    defaultMessage: 'Ship To',
    id: 'eXpVHV',
  },
  expectedDelivery: {
    description: 'shipmentDetails.labels.expectedDelivery',
    defaultMessage: 'Expected Delivery',
    id: 'PnmWWo',
  },
  confirmation: {
    description: 'shipmentDetails.labels.confirmation',
    defaultMessage: 'Confirmation',
    id: '4i5m78',
  },
  insurance: {
    description: 'shipmentDetails.labels.insurance',
    defaultMessage: 'Insurance',
    id: 'cYT58a',
  },
  packageType: {
    description: 'shipmentDetails.labels.packageType',
    defaultMessage: 'Package Type',
    id: 'qJ31R6',
  },
  dimensions: {
    description: 'shipmentDetails.labels.dimensions',
    defaultMessage: 'Dimensions',
    id: 'UzaDRC',
  },
  weight: {
    description: 'shipmentDetails.labels.weight',
    defaultMessage: 'Weight',
    id: '6KO3QP',
  },
  dateShipped: {
    description: 'shipmentDetails.labels.dateShipped',
    defaultMessage: 'Date Shipped',
    id: 'F/lPZ4',
  },
  multiple: {
    description: 'shipmentDetails.labels.multiple',
    defaultMessage: '(Multiple)',
    id: '41VhB4',
  },
});

const renderShippingOptions = (options, returnNote) => {
  const optionNames = OtherShippingOptionsService.convertEnabledOptionsToAbbreviatedNameArray(options);
  if (optionNames.length && !returnNote) {
    const optionNameMessages = optionNames.map((optionName) => <FormattedMessage key={GuidService.newGuid()} {...optionName} />);
    return (
      <div className={style['shipment-details-options']}>
        <label>
          <div className={style['shipping-options-label']}>
            <FormattedMessage defaultMessage='Shipping Options' description='shipmentDetails.shippingOptions' id='bh8JAy' />
          </div>
        </label>
        <div>
          <FormattedList style='short' type='unit' value={optionNameMessages} />
        </div>
      </div>
    );
  }

  if (returnNote && returnNote.length) {
    return (
      <div className={style['shipment-details-options']}>
        <label>
          <FormattedMessage defaultMessage='Return note' description='shipmentDetails.returnNote' id='8T+QAV' />
        </label>
        <div>{returnNote}</div>
      </div>
    );
  }
  return null;
};

const propsForLayout = (fulfillmentPlan, fulfillment, shipFrom, packageType, shipTo, returnShipment, weightUnit, dimensionsUnit) => {
  if (FulfillmentPlanService.isExternal(fulfillmentPlan)) {
    return ShipmentDetailsHelper.thirdPartyFulfillmentPackagingDetails(fulfillmentPlan, fulfillment, shipFrom);
  }
  if (returnShipment) {
    const returnShipTo = !shipTo ? get(returnShipment, 'returnConfiguration.shipFromName') : shipTo;
    return ShipmentDetailsHelper.returnShipmentPackagingDetails(returnShipment, returnShipTo, packageType, dimensionsUnit, weightUnit);
  }

  const returnShipFrom = !shipFrom ? get(fulfillment, 'shipFrom.name') : shipFrom;
  return ShipmentDetailsHelper.shipmentPackagingDetails(
    fulfillmentPlan,
    fulfillment,
    returnShipFrom,
    packageType,
    dimensionsUnit,
    weightUnit,
  );
};

/**
 * @typedef {import('../../prop-types/fulfillment-plan').FulfillmentPlanPropType} FulfillmentPlanPropType
 * @typedef {import('../../prop-types/return-shipment').ReturnShipmentPropType} ReturnShipmentPropType
 * @typedef {import('../../prop-types/fulfillment').FulfillmentPropType} FulfillmentPropType
 */
/**
 * A component for displaying shipment details.
 * Allows selection of individual packages or package sets based on feature flags.
 * @param {object} props - The component props
 * @param {FulfillmentPlanPropType} [props.fulfillmentPlan]
 * @param {FulfillmentPropType[]} [props.fulfillments] - Array of fulfillments associated with the shipment
 * @param {string} [props.shipFrom] - The address from which the shipment is shipped
 * @param {string} [props.packageType] - The type of package used for the shipment
 * @param {string} [props.shipTo] - The address to which the
 * @param {ReturnShipmentPropType[]} [props.returnShipment] - Array of fulfillments associated with the shipment
 * @returns {React.ReactComponentElement} The rendered PackageDropdown component
 */
const ShipmentDetails = ({
  fulfillmentPlan = {},
  fulfillments = [],
  returnShipment = null,
  shipFrom = '',
  packageType = '',
  shipTo = '',
}) => {
  const dimensionsUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  // TODO data returned from server in currently incorrect around labelCost, find fulfillment and use that data
  const fulfillment = fulfillments.find((fulfill) => fulfill.fulfillmentPlanId === fulfillmentPlan.fulfillmentPlanId) || {};
  const packageCount = get(fulfillment, 'packages.length', 0);
  const returnNote = get(returnShipment, 'note', false);
  const layoutProps = propsForLayout(
    fulfillmentPlan,
    fulfillment,
    shipFrom,
    packageType,
    shipTo,
    returnShipment,
    weightUnit,
    dimensionsUnit,
  );
  const intl = useIntl();
  return (
    <div className={style['shipment-details-container']}>
      <div className={style['shipment-columns']}>
        {Object.keys(layout)
          .map((key) => layout[key])
          .map((column, index) => (
            <div key={index} className={style['shipment-details-column']}>
              {column
                .filter((detail) => Object.prototype.hasOwnProperty.call(layoutProps, detail.id))
                .map(({ id, showMultiple }) => {
                  const value = showMultiple(packageCount) ? intl.formatMessage(messages.multiple) : layoutProps[id];
                  return <LabeledData key={id} label={intl.formatMessage(messages[id])} value={value} />;
                })}
            </div>
          ))}
      </div>
      {renderShippingOptions(fulfillmentPlan.options, returnNote)}
    </div>
  );
};

export default React.memo(ShipmentDetails);
